import { createContext, useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { announcementsData$ } from '../../../reduxStore/selectors/announcementSelectors';
import { getActiveAnnouncements } from '../../../reduxStore/actions/announcementActionsHelper';
import { usersData$ } from '../../../reduxStore/selectors/userSelectors';

const AnnouncementContext = createContext();

const ANNOUNCEMENT_SHOWN_KEY = 'prepal_announcements_shown';

export const AnnouncementProvider = ({ children }) => {
    const [showAnnouncements, setShowAnnouncements] = useState(false);
    const announcements = useSelector(announcementsData$);
    const userData = useSelector(usersData$);
    const dispatch = useDispatch();

    // Fetch announcements when user is available
    useEffect(() => {
        if (userData?.id) {
            dispatch(getActiveAnnouncements.request());
        }
    }, [userData?.id, dispatch]);

    // Auto-show announcements on first login
    useEffect(() => {
        const announcementsShown = sessionStorage.getItem(ANNOUNCEMENT_SHOWN_KEY);

        if (!announcementsShown && userData?.id && announcements.length > 0) {
            const timer = setTimeout(() => {
                setShowAnnouncements(true);
                sessionStorage.setItem(ANNOUNCEMENT_SHOWN_KEY, 'true');
            }, 150);

            return () => clearTimeout(timer);
        }
    }, [userData?.id, announcements.length]);

    const openAnnouncements = () => {
        // Ensure announcements are fetched before opening
        if (userData?.id && announcements.length === 0) {
            dispatch(getActiveAnnouncements.request());
        }
        // Open immediately without any delay
        setShowAnnouncements(true);
    };

    const closeAnnouncements = () => {
        setShowAnnouncements(false);
    };

    // Check if there are unread announcements
    const hasUnreadAnnouncements = announcements.length > 0;

    const value = {
        showAnnouncements,
        openAnnouncements,
        closeAnnouncements,
        hasUnreadAnnouncements,
        announcementsCount: announcements.length
    };

    return (
        <AnnouncementContext.Provider value={value}>
            {children}
        </AnnouncementContext.Provider>
    );
};

export const useAnnouncements = () => {
    const context = useContext(AnnouncementContext);
    if (!context) {
        throw new Error('useAnnouncements must be used within an AnnouncementProvider');
    }
    return context;
};

export const useOptionalAnnouncements = () => {
    const context = useContext(AnnouncementContext);

    if (!context) {
        return {
            showAnnouncements: false,
            openAnnouncements: () => { },
            closeAnnouncements: () => { },
            hasUnreadAnnouncements: false,
            announcementsCount: 0,
        };
    }

    return context;
};
